import React, { Component } from 'react';
import thunk from 'redux-thunk';
import { connect } from 'react-redux';
import { createStore, applyMiddleware, combineReducers } from 'redux';
import PropTypes from 'prop-types';
import FocusTrap from 'focus-trap-react';
import searchReducer from '../reducers/search';
import getSuggestion from '../selectors';
import MobileHamburgerButton from '../components/MobileHamburgerButton';
import NavLogo from '../components/NavLogo';
import SearchForm from '../components/SearchForm';
import getSearchUrl from '../helpers/getSearchUrl';
import { getAutoSuggestion, resetIneligibleAddOns } from '../actions';
import NavigationMain from '../../../navigation/NavigationMain/containers/NavigationMain';
import MobileNavMenu from '../../../navigation/MobileNavMenu/containers/MobileNavMenu';
import CartMini from '../../../cart/CartMini/CartMini';
import AttributeSelector from '../../../shared/components/AttributeSelector/containers/AttributeSelector';
import { escapeRegExpSearch } from '../helpers/escapeRegExp';
import MediaQueryListener from '../../../shared/helpers/MediaQueryListener';
import getGlobalFromWindow from '../../../shared/helpers/getGlobalFromWindow';
import OpcoNavigator from '../../../shared/components/OpcoNavigator/containers/OpcoNavigator';
import classNames from 'classnames';


const mapStateToProps = state => ({
  suggestions: getSuggestion(state),
});

class SiteHeader extends Component {
  static getDomId() {
    return 'layout_site_header';
  }

  static getStore() {
    const middleware = [thunk];

    return createStore(
      combineReducers({ search: searchReducer }),
      { search: {} },
      applyMiddleware(...middleware),
    );
  }

  constructor(props) {
    super(props);
    this.headerRef = React.createRef();
    this.formRef = React.createRef();
    this.searchBarRef = React.createRef();
    this.handlers = null;
    this.headerBottom = 10;
    this.cartMini = window.seMarketplace.cart_mini;
    this.searchOmnibox = window.seMarketplace.search_omnibox;
    this.siteGlobals = window.seMarketplace.site_globals;
    this.navigationMain = window.seMarketplace.navigation_main;
    this.layoutSiteHeader = window.seMarketplace.layout_site_header;
    this.isMultiOpCo = getGlobalFromWindow('isMultiOpCo');
    this.baseUrl = getGlobalFromWindow('homeURL').replace('/home', '');
    this.OpcoNavigatorConfigSet = getGlobalFromWindow('OpcoNavigator') !== null;

    this.toggleDrawerOpen = this.toggleDrawerOpen.bind(this);
    this.toggleSuggestions = this.toggleSuggestions.bind(this);
    this.handleQueryChange = this.handleQueryChange.bind(this);
    this.handleQuerySubmit = this.handleQuerySubmit.bind(this);
    this.updateCartNumItems = this.updateCartNumItems.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.addEventListeners = this.addEventListeners.bind(this);
    this.updateAttributeSelector = this.updateAttributeSelector.bind(this);
    this.renderOpCoSelector = this.renderOpCoSelector.bind(this);
    this.mq = new MediaQueryListener(() => this.forceUpdate());


    this.state = {
      drawerIsOpen: false,
      showSuggestions: false,
      cartMini: this.cartMini,
    };
  }

  componentDidMount() {
    const handlers = {
      cartItemsChanged: this.updateCartNumItems,
    };

    this.handlers = window.subscribeToMarketplaceEvents(handlers);
  }

  componentWillUnmount() {
    this.addEventListeners(false);
    window.unsubscribeFromMarketplaceEvents(this.handlers);
  }

  debounce(callBackFunc, wait) {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => callBackFunc(), wait);
  }

  updateCartNumItems(event) {
    const { detail: quantity } = event;
    const { cartMini } = this.state;

    this.setState({
      cartMini: {
        ...cartMini,
        quantity,
      },
    });
  }

  toggleDrawerOpen() {
    const { drawerIsOpen } = this.state;

    window.scrollTo(0, 0);
    if (this.headerRef.current) {
      let searchBarHeight = 0;

      if (this.searchBarRef.current) {
        searchBarHeight = this.searchBarRef.current.getBoundingClientRect().height;
      }
      this.headerBottom = this.headerRef.current.getBoundingClientRect().bottom - searchBarHeight;
    }
    document.body.classList.toggle('no-scroll', !drawerIsOpen);
    this.setState({ drawerIsOpen: !drawerIsOpen });
  }

  handleQueryChange({ target: { value: query } }) {
    const wait = 250;
    const { dispatch } = this.props;
    const { searchOmnibox: { autoSuggestUrl } } = this;

    if (query && query.length > 2) {
      const escapedQuery = escapeRegExpSearch(query);

      this.debounce(() => dispatch(getAutoSuggestion(autoSuggestUrl, escapedQuery)), wait);
    }
  }

  handleQuerySubmit(event) {
    event.preventDefault();
    const { searchOmnibox: { action } } = this;
    const { target: { elements: { search: { value } } } } = event;

    if (value) {
      const escapedQuery = escapeRegExpSearch(value);

      window.location.assign(getSearchUrl(action, escapedQuery));
    }
  }

  toggleSuggestions(toggle) {
    const wait = 1;

    this.debounce(() => {
      this.addEventListeners(toggle);
      this.setState({ showSuggestions: toggle });
    }, wait);
  }

  addEventListeners(toggle) {
    if (toggle) {
      window.addEventListener('click', this.handleOutsideClick);
      window.addEventListener('touchstart', this.handleOutsideClick);
    } else {
      window.removeEventListener('click', this.handleOutsideClick);
      window.removeEventListener('touchstart', this.handleOutsideClick);
    }
  }

  handleOutsideClick({ target }) {
    if (!this.formRef.current.contains(target)) {
      this.toggleSuggestions(false);
    }
  }

  updateAttributeSelector() {
    const { siteGlobals: { addOnResetIneligibleUrl } } = this;
    const { dispatch } = this.props;

    return dispatch(resetIneligibleAddOns(addOnResetIneligibleUrl));
  }

  renderOpCoSelector() {
    const {
      programConfig,
      currentSiteConfig,
      content,
      autoOpenParam,
    } = getGlobalFromWindow('OpcoNavigator');
    const emptyCart = !this.cartMini || this.cartMini.quantity === 0;

    return (
      (
        <OpcoNavigator
          templateId="primary"
          redirectTo={`${this.baseUrl}/opco?siteId=`}
          showDisclaimer={!emptyCart}
          programConfig={programConfig}
          currentSiteConfig={currentSiteConfig}
          content={content}
          autoOpenParam={autoOpenParam}
        />
      )
    );
  }

  // eslint-disable-next-line complexity
  render() {
    const {
      siteGlobals,
      navigationMain,
      layoutSiteHeader,
      searchOmnibox: { placeholder },
    } = this;
    const {
      cartMini,
      drawerIsOpen,
      showSuggestions,
    } = this.state;
    const { suggestions } = this.props;
    const cartExists = cartMini && cartMini.url;
    const breakPoint = this.mq.getBreakPoint();
    const hideNavigation = layoutSiteHeader.hideNavigation;
    const hideSearch = layoutSiteHeader.hideSearch;

    const site_header = classNames({
      'site-header': true,
      'site-header-basic': hideNavigation && hideSearch
    });

    if (!cartExists) {
      return (
        <div className={site_header}>
          <div className="site-header__utility">
            <NavLogo siteGlobals={siteGlobals} logo={layoutSiteHeader.logo} />
          </div>
        </div>
      );
    }

    return (
      <div className={site_header} ref={this.headerRef}>
        <FocusTrap active={drawerIsOpen}>
          <div className="site-header__utility-wrapper">
            <div className="site-header__utility" aria-label="Site navigation">
              <NavLogo siteGlobals={siteGlobals} logo={layoutSiteHeader.logo} />
              {(breakPoint && breakPoint.value >= 2) && (
                <div className="site-header__opco-nav">
                  {(this.isMultiOpCo && this.OpcoNavigatorConfigSet) && this.renderOpCoSelector()}
                  <AttributeSelector updateAction={this.updateAttributeSelector} />
                </div>
              )}
              <div className="site-header__search" ref={this.searchBarRef}>
                {!hideSearch && <SearchForm
                  formRef={this.formRef}
                  placeholder={placeholder}
                  suggestions={suggestions}
                  showSuggestions={showSuggestions}
                  toggleSuggestions={this.toggleSuggestions}
                  handleQueryChange={this.handleQueryChange}
                  handleQuerySubmit={this.handleQuerySubmit}
                />}
              </div>
              <div className="site-header__cart"><CartMini {...cartMini} /></div>
              <MobileHamburgerButton
                drawerIsOpen={drawerIsOpen}
                toggleDrawerOpen={this.toggleDrawerOpen}
              />
              <MobileNavMenu
                toggleDrawerOpen={this.toggleDrawerOpen}
                isOpen={drawerIsOpen}
                top={this.headerBottom}
                navItems={navigationMain}
              />
            </div>
            {!hideNavigation &&
              <div className="site-header__nav-main"><NavigationMain categories={navigationMain} /></div>
            }
          </div>
        </FocusTrap>
        {(breakPoint && breakPoint.value < 2) && (
          <div className="site-header__opco-nav">
            {(this.isMultiOpCo && this.OpcoNavigatorConfigSet) && this.renderOpCoSelector()}
            <AttributeSelector updateAction={this.updateAttributeSelector} />
          </div>
        )}
      </div>
    );
  }
}

SiteHeader.propTypes = {
  dispatch: PropTypes.func.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};


export default connect(mapStateToProps)(SiteHeader);
